import {
  type scenarioActivationResponseSchema,
  type scenarioAlertResponseSchema,
  type scenarioChangeSummarySchema,
  type scenarioMetricsSchema,
  scenarioResponseSchema,
  scenarioSchema,
} from '@tempo-io/adaptive-planner-dto';
import { z } from 'zod';

export const ScenarioConstraintsScale = {
  Low: 'LOW',
  Medium: 'MEDIUM',
  High: 'HIGH',
  NoOp: 'NOOP',
} as const;

export const ScenarioStatus = {
  Created: 'CREATED',
  RescheduleQueued: 'RESCHEDULE_QUEUED',
  MLInitiated: 'ML_INITIATED',
  MLValidationFailed: 'ML_VALIDATION_FAILED',
  MLValidationSuccess: 'ML_VALIDATION_SUCCESS',
  MLReceived: 'ML_RECEIVED',
  Timeout: 'TIMEOUT',
} as const;

export const ExecutionState = {
  Success: 'SUCCESS',
  InProgress: 'IN_PROGRESS',
  Failed: 'FAILED',
} as const;

export const ScenarioValidation = {
  MissingTeam: 'MISSING_TEAM',
  EmptyTeam: 'EMPTY_TEAM',
  NoAvailability: 'NO_AVAILABILITY',
  TeamUnavailable: 'TEAM_UNAVAILABLE',
  MissingEstimation: 'MISSING_ESTIMATION',
  ItemIsDone: 'ITEM_IS_DONE',
  AllItemsUnschedulable: 'ALL_ITEMS_UNSCHEDULABLE',
} as const;

export type ScenarioConstraintsScale = Scenario['priorityFlexibility'];

export type ScenarioStatus = Scenario['status'];

export type ExecutionState = Scenario['executionState'];

export type ScenarioValidation = Scenario['schedulingValidations'][number];

export const SCENARIO_ALERT_TYPES = {
  ITEM_STORE_SCHEDULE_CHANGES: 'ITEM_STORE_SCHEDULE_CHANGES',
  ITEM_STORE_ITEMS_ADDED: 'ITEM_STORE_ITEMS_ADDED',
  ITEM_STORE_ITEMS_DELETED: 'ITEM_STORE_ITEMS_DELETED',
  ITEM_STORE_DEPENDENCY_CHANGES: 'ITEM_STORE_DEPENDENCY_CHANGES',
  SCENARIO_ITEM_MODIFIED: 'SCENARIO_ITEM_MODIFIED',
  TEAM_COMPOSITION_UPDATED: 'TEAM_COMPOSITION_UPDATED',
  ESTIMATION_EFFORT_UPDATED: 'ESTIMATION_EFFORT_UPDATED',
  ITEMS_UNDER_DONE_ANCESTOR: 'ITEMS_UNDER_DONE_ANCESTOR',
  TEAM_WITHOUT_MEMBERS: 'TEAM_WITHOUT_MEMBERS',
} as const;

// Softened relative to the generated schema, which requires it. isPortfolioBaseline ships in
// NEX-14946 (#12285); environments still on an older build omit it, and the UI derives the same
// flag from the constraint values, so requiring it would fail the whole route for no gain.
// isMLImputed is the same kind of computed getter but predates that deploy, so it stays required —
// the asymmetry is deliberate, not an oversight.
// Remove this override (and the accompanying it.todo) once #12285 is deployed everywhere.
export const parsedScenarioSchema = scenarioSchema.extend({
  isPortfolioBaseline: z.boolean().optional(),
});

export const parsedScenarioResponseSchema = scenarioResponseSchema.extend({
  scenario: parsedScenarioSchema,
});

export type ScenarioAlert = z.infer<typeof scenarioAlertResponseSchema>;

export type ScenarioChangeSummary = z.infer<typeof scenarioChangeSummarySchema>;

export type ScenarioMetrics = z.infer<typeof scenarioMetricsSchema>;

export type Scenario = z.infer<typeof parsedScenarioSchema>;

export type ScenarioResponse = z.infer<typeof parsedScenarioResponseSchema>;

export type ScenarioActivationResponse = z.infer<typeof scenarioActivationResponseSchema>;
